import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import {
  Dropdown,
  Menu,
  message,
  Popconfirm,
  Select,
  Typography,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  IMPORT_VEHICLE,
  VEHICLE_INFORMATION,
} from "../../../apiServices/API_ENDPOINTS";
import { deleteData, getData } from "../../../apiServices/common";
import ListTable from "common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import {
  GOODS_DELIVERY_CREATE_PERMISSION,
  GOODS_DELIVERY_DELETE_PERMISSION,
  GOODS_DELIVERY_EDIT_PERMISSION,
} from "../../../routes/permissions";
import { hasPermission } from "../../../utils/functions";
import CustomFilter from "../../Common/CustomFilter";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";

const ImportVehicleInformation = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const { Option } = Select;
  const { Title } = Typography;

  // Router
  const navigate = useNavigate();

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllVehicleStatus = useCallback(
    async (filterValues) => {
      setLoading(true);
      setError(null);

      const query = `${VEHICLE_INFORMATION}?page=${currentPage}`;

      const response = await getData(query, false, filterValues);

      if (response && response?.data?.code === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        setError("Could not load this list.");
        message.error("Error getting vehicle information data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllVehicleStatus();
  }, [refresh, getAllVehicleStatus]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedVehicle = async (id) => {
    setLoading(true);
    const response = await deleteData(`${IMPORT_VEHICLE}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Vehicle Information Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Error deleting vehicle information");
    }
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Type of Shipment",
      dataIndex: "type_of_shipment",
      key: "type_of_shipment",
    },
    {
      title: "LC Number",
      dataIndex: ["b2b_lc_info", "b2b_lc_number"],
      key: "b2b_lc_number",
      render: (value) => value || "N/A",
    },
    {
      title: "Salecontract",
      dataIndex: ["sales_contract_info", "reference_no"],
      key: "reference_no",
      render: (value) => value || "N/A",
    },
    {
      title: "Invoice Number",
      dataIndex: ["invoice_info", "invoice_number"],
      key: "invoice_number",
    },
    {
      title: "Vehicle Quantity",
      dataIndex: "vehicle_qty",
      key: "vehicle_qty",
    },
    {
      title: "Transporter",
      dataIndex: ["vehicle_info", "transporter"],
      key: "transporter",
    },
    {
      title: "Place of Loading",
      dataIndex: ["vehicle_info", "loading_place"],
      key: "loading_place",
    },
    {
      title: "Place of Arrival",
      dataIndex: ["vehicle_info", "delivery_place"],
      key: "delivery_place",
    },
    // {
    //   title: "Status",
    //   dataIndex: ["vehicle_info", "status_text"],
    //   key: "status",
    // },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedVehicle(record?.vehicle_info?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([
                      GOODS_DELIVERY_DELETE_PERMISSION,
                    ]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            navigate(
                              `/import/edit-vehicle-information/${record?.vehicle_info?.id}`,
                            );
                          }}
                        >
                          {record?.vehicle_info?.status === "Completed"
                            ? "Edit"
                            : "Set Vehicle Number"}
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([GOODS_DELIVERY_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          />
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        tableData={tableData}
        fetchTableData={getAllVehicleStatus}
        filterName="import_vehicle_info"
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                disabled={!hasPermission([GOODS_DELIVERY_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([GOODS_DELIVERY_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                handleAddItemOpen={() =>
                  navigate("/import/add-vehicle-information")
                }
              />
            }
            title={<Title level={5}>Vehicle Information</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={error}
                onRetry={() => getAllVehicleStatus()}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default ImportVehicleInformation;
